// MCMItemCard — MCM-styled item editor sheet. Drop-in replacement
// for the legacy src/components/ItemCard.jsx mount when an MCM-grid
// card opens. Same external prop shape (item / pantry / userId /
// isAdmin / familyIds / onUpdate / onDelete / onClose) so App.jsx
// can swap freely.
//
// v1 covers the day-to-day edit path: rename, brand, identity-axis
// chips (canonical / category / state / stored-in), package size +
// remaining gauge, expires, location segment, delete, close.
// Deferred to a follow-up: nutrition deep-dive, ingredient
// composition tags, AI enrichment, cook-log provenance, reheat /
// "I ate this" / schedule-eating sheets. Tap an item that needs
// those flows and the legacy ItemCard still has the answer; this
// editor focuses on the 80% case.
//
// Patches fire onUpdate({field: value}) per-change so changes are
// persisted immediately. There's no Save button — the bottom row
// is Delete + Done.

import { useEffect, useMemo, useRef, useState } from "react";
import { motion, useDragControls } from "framer-motion";
import { Kicker, withAlpha } from "./primitives";
import { useTheme, THEME_TRANSITION } from "./theme";
import {
  font, space, radius, axis, prose,
  stack, inline, divider, field, ctaButton, ghostButton,
} from "./tokens";
import {
  findIngredient, INGREDIENTS, dbCanonicalsSnapshot,
  statesForIngredient, STATE_LABELS, getIngredientInfo,
  cutsForIngredient, cutLabel, hubForIngredient, findHub,
} from "../../data/ingredients";
import { useIngredientInfo } from "../../lib/useIngredientInfo";
import { useProfile } from "../../lib/useProfile";
import StapleStar from "../../components/StapleStar";
import EnrichmentButton from "../../components/EnrichmentButton";
import { dietaryWarningsForRow } from "../../lib/dietaryWarnings";
import { canonicalImageUrlFor } from "../../lib/canonicalIcons";
import { useBodyScrollLock } from "../../lib/useBodyScrollLock";
import { useSheetDismissAtTop } from "../../lib/useSheetDismissAtTop";
import {
  LOCATIONS, shelfLifeFor, formatDaysChip, daysChipColor,
  buildDisplayName, getItemClaims,
  autoMigratePatchOnOpen, openedNounForState,
} from "./helpers";
import { useToast } from "../../lib/toast";
import { rememberBarcodeCorrection } from "../../lib/barcodeCorrections";
import { LOCATION_DOT } from "./FloatingLocationDock";
import { AddDraftHeaderPills } from "./AddDraftHeaderPills";
import { AddDraftPickers } from "./AddDraftPickers";
import { BrandPickerSheet } from "./BrandPickerSheet";
import { KidRequestableSection } from "./KidRequestableSection";
import EatIntentSheet from "../../components/EatIntentSheet";
import ReheatMode from "../../components/ReheatMode";
import IAteThisSheet from "../../components/IAteThisSheet";
import { formatReheatSummary } from "../../data/recipes/schema";
import { suggestCookInstructions } from "../../lib/suggestCookInstructions";
import NutritionLabelScanner from "../../components/NutritionLabelScanner";
import NutritionOverrideSheet from "../../components/NutritionOverrideSheet";
import { useBrandNutrition } from "../../lib/useBrandNutrition";

export function MCMItemCard({
  item,
  pantry,
  userId,
  isAdmin,
  familyIds,
  onUpdate,
  onDelete,
  onClose,
}) {
  const { theme } = useTheme();
  const { dbMap } = useIngredientInfo();
  const toast = useToast();

  // Local state mirrors the row. Each field's setter also fires
  // onUpdate immediately so the parent persists the change without
  // a Save button. We don't gate on dirty/clean state — the editor
  // is itself the source of truth while open.
  const [name,        setNameState]        = useState(item.name        || "");
  const [brand,       setBrandState]       = useState(item.brand       || "");
  const [canonicalId, setCanonicalIdState] = useState(item.canonicalId || null);
  const [typeId,      setTypeIdState]      = useState(item.typeId      || null);
  const [tileId,      setTileIdState]      = useState(item.tileId      || null);
  const [location,    setLocationState]    = useState(item.location    || "fridge");
  const [stateAxis,   setStateAxisState]   = useState(item.state       || null);
  // Cut — anatomical / butchery slot for meats. Only renders a chip
  // when the canonical's hub has CUTS_FOR entries (chicken / beef /
  // pork / turkey today). Surfaces in the derived display name when
  // set so a row like (canonical=beef, cut=ny_strip) reads as
  // "NY Strip Beef" and a recipe asking for "ribeye" can pair against
  // a beef row tagged with cut=ribeye even though the canonical id
  // is the species hub. Picker is wired below in AddDraftPickers.
  const [cut, setCutState] = useState(item.cut || null);
  const [amount,      setAmountState]      = useState(item.amount ?? null);
  const [max,         setMaxState]         = useState(item.max    ?? null);
  const [unit,        setUnitState]        = useState(item.unit        || "");
  const [expiresAt,   setExpiresAtState]   = useState(item.expiresAt   || null);
  // Kid-requestable axis (migration 0244). Lives inline in this
  // sheet instead of buried under Settings → Kids Mode → KID ITEMS
  // so parents can flag a specific pantry row while editing it.
  const [kidVisible,  setKidVisibleState]  = useState(!!item.kidVisible);
  const [kidLabel,    setKidLabelState]    = useState(item.kidLabel    || null);
  const [kidKind,     setKidKindState]     = useState(item.kidKind     || null);

  // AddDraftPickers expects override flags so we feed it true-by-
  // default — every value here is already user-set (we're editing
  // an existing row), so the cascade never wants to overwrite.
  const setOverridden = () => {};
  const allCanonicals = useMemo(
    () => [...INGREDIENTS, ...dbCanonicalsSnapshot()],
    [dbMap]
  );

  // Auto-days for the expires picker's Auto preset — uses shelf-
  // life data anchored to this item's location.
  const autoDays = useMemo(() => {
    if (!canonicalId) return null;
    return shelfLifeFor(canonicalId, location, { opened: false });
  }, [canonicalId, location]);

  // Merged enrichment — admin-curated db row wins, bundled JS
  // INGREDIENT_INFO fills the gap. Returns null when neither has
  // anything to say (free-text rows with no canonical, or canonicals
  // that haven't been enriched yet — those stay quiet rather than
  // showing empty section labels).
  const enrichment = useMemo(() => {
    if (!canonicalId) return null;
    const ing    = findIngredient(canonicalId);
    const dbInfo = dbMap?.[canonicalId] || null;
    return getIngredientInfo(ing, dbInfo);
  }, [canonicalId, dbMap]);
  const hasInfo =
    enrichment
    && (enrichment.nutrition
      || enrichment.description
      || enrichment.flavorProfile
      || enrichment.prepTips
      || enrichment.origin
      || enrichment.culturalNotes
      || (Array.isArray(enrichment.pairs) && enrichment.pairs.length > 0));

  // Picker latch (canonical / category / state / tile / unit / expires)
  const [pickerOpen, setPickerOpen] = useState(null);
  const [brandEditing, setBrandEditing] = useState(false);

  // Drag-to-dismiss controls. Manual dragControls (vs. the default
  // listener-on-the-whole-element) so the sheet body stays
  // scrollable; the top grabber pill OR a pull-down-from-the-top
  // gesture starts a drag. Pulling past DRAG_DISMISS_PX or
  // releasing with enough downward velocity calls onClose;
  // otherwise framer springs it back to y=0 on release.
  const dragControls = useDragControls();
  const sheetRef = useRef(null);
  // Pull-past-the-top dismiss — distance-thresholded. When the sheet
  // is scrolled to the top and the user's finger moves more than
  // 100 px downward (skipping touches that land on interactive
  // elements), fire onClose directly. iOS's native rubber-band
  // gives the visual feedback during the pull; no framer-drag
  // handoff or release-to-confirm step.
  const dismissHandlers = useSheetDismissAtTop(sheetRef, () => onClose && onClose());

  // Confirm-delete inline gate so a stray tap on the destructive
  // action doesn't blow away the row. First tap arms; second tap
  // commits. Auto-disarms after 4s.
  const [deleteArmed, setDeleteArmed] = useState(false);
  useEffect(() => {
    if (!deleteArmed) return;
    const t = setTimeout(() => setDeleteArmed(false), 4000);
    return () => clearTimeout(t);
  }, [deleteArmed]);

  // Eat-flow phase machine — gated by tapping EAT on the footer.
  //   null     — closed.
  //   "intent" — EatIntentSheet (REHEAT FIRST vs ALREADY ATE IT, or
  //              GENERATE REHEAT WITH AI when no instructions exist).
  //              Only opens for items with cookable context (leftovers
  //              with per-item cook_instructions, or kind=meal rows).
  //   "reheat" — full-screen ReheatMode walkthrough. Reached from
  //              REHEAT FIRST or after a successful AI generation.
  //   "log"    — IAteThisSheet for amount + meal slot + macro log.
  //              Direct entry for raw ingredients; final stop after
  //              REHEAT walkthrough for leftovers.
  const [eatPhase, setEatPhase] = useState(null);
  // Live cookInstructions — initial value mirrors the row, but the
  // GENERATE REHEAT path lands a fresh block here BEFORE ReheatMode
  // mounts so the walkthrough renders with the new steps without
  // round-tripping through the parent's onUpdate + re-fetch.
  const [liveCookInstructions, setLiveCookInstructions] = useState(
    item.cookInstructions || null,
  );
  // Has-cookable-context discriminator. The EAT button routes
  // through EatIntentSheet (with REHEAT FIRST or GENERATE REHEAT
  // options) only for rows that plausibly need reheating; raw
  // ingredients skip the gate entirely and go straight to log.
  // Three signals catch the cookable rows:
  //   1. Per-item cookInstructions (migration 0125) — authored
  //      reheat block is the strongest signal.
  //   2. Fridge tile "leftovers" — user explicitly placed the row
  //      in the leftovers tile, which matches "anything cooked and
  //      stored" in the registry.
  //   3. Freezer tile "frozen_meal_prep" — cooked meals / batch
  //      portions intended to be reheated.
  // Meal-kind rows from CookComplete are NOT gated here because
  // IAteThisSheet has its own internal reheat phase driven by the
  // source recipe's reheat block — routing them through
  // EatIntentSheet would shadow that.
  const hasCookableContext =
    !!liveCookInstructions
    || tileId === "leftovers"
    || tileId === "frozen_meal_prep";

  const handleEatTap = () => {
    setEatPhase(hasCookableContext ? "intent" : "log");
  };

  // Async — feeds EatIntentSheet's onGenerate prop. Calls Claude via
  // the shared suggest-cook-instructions edge function, persists the
  // result on the row (so future opens skip the regen), then
  // transitions to the reheat walkthrough.
  const handleGenerateReheat = async () => {
    const result = await suggestCookInstructions({
      name:        item.name,
      canonicalId: canonicalId || null,
      brand:       brand || null,
      state:       stateAxis || null,
      cut:         cut || null,
      category:    item.category || null,
    });
    if (result?.error) {
      throw new Error(result.error);
    }
    const fresh = result?.cookInstructions || null;
    if (!fresh || !Array.isArray(fresh.steps) || fresh.steps.length === 0) {
      throw new Error("Generated reheat is empty — try again.");
    }
    setLiveCookInstructions(fresh);
    onUpdate && onUpdate({ cookInstructions: fresh });
    setEatPhase("reheat");
  };

  const reheatRecipe = liveCookInstructions
    ? {
        title:  item.name || item.canonicalName || "Reheat",
        emoji:  item.emoji || null,
        steps:  liveCookInstructions.steps || [],
        reheat: liveCookInstructions,
      }
    : null;

  // ── Nutrition label scan flow ───────────────────────────────────────
  // Three-phase state machine for "📸 Scan Nutrition Label":
  //   null       — closed
  //   "scanner"  — NutritionLabelScanner — photo capture + Sonnet OCR
  //   "override" — NutritionOverrideSheet — review/edit pre-filled
  //                with the scan result, then SAVE persists to the
  //                row's nutritionOverride + (optionally) the shared
  //                brand_nutrition cache for everyone else's benefit.
  const [nutritionPhase, setNutritionPhase] = useState(null);
  const [nutritionScanPayload, setNutritionScanPayload] = useState(null);
  const { upsert: upsertBrandNutrition } = useBrandNutrition();

  const handleNutritionScanComplete = (payload) => {
    setNutritionScanPayload(payload);
    setNutritionPhase("override");
  };

  const handleNutritionScanManualFallback = () => {
    setNutritionScanPayload(null);
    setNutritionPhase("override");
  };

  const handleNutritionSave = async (payload) => {
    if (!payload) {
      // User cleared the override.
      onUpdate && onUpdate({ nutritionOverride: null });
      setNutritionPhase(null);
      setNutritionScanPayload(null);
      return;
    }
    // Persist the per-row override locally first — instant UI update.
    onUpdate && onUpdate({ nutritionOverride: payload.nutrition });
    // ALSO teach brand_nutrition at the shared tier when (a) the user
    // didn't hand-edit too much (preserving the scan's accuracy) and
    // (b) we have canonical + brand to key on. Failure here is fine
    // — the row-level override still landed.
    const canonical = canonicalId;
    const brandStr  = brand?.trim();
    if (
      canonical && brandStr
      && payload.provenance === "label_scan"
      && (payload.dirtyCount ?? 0) <= 2
    ) {
      try {
        await upsertBrandNutrition({
          canonicalId: canonical,
          brand:       brandStr,
          nutrition:   payload.nutrition,
          barcode:     item.barcodeUpc || null,
          source:      "label_scan",
          sourceId:    payload.scanId || null,
          confidence:  90,
        });
      } catch (e) {
        console.warn("[MCMItemCard] brand_nutrition teach failed:", e?.message || e);
      }
    }
    setNutritionPhase(null);
    setNutritionScanPayload(null);
  };

  // Field-by-field setters — update local + fire patch.
  const update = (patch) => onUpdate && onUpdate(patch);
  // Self-teaching cascade (CLAUDE.md): every identity-axis edit on a
  // row that arrived via UPC scan ALSO teaches the barcode-correction
  // memory, so the next scan of this product lands with the fix
  // pre-filled (family tier; global tier when isAdmin). Fire-and-
  // forget — a teach failure never blocks the row edit itself.
  const teachCorrection = (patch) => {
    const upc = item?.barcodeUpc;
    if (!upc || !userId) return;
    rememberBarcodeCorrection({
      userId,
      isAdmin: !!isAdmin,
      barcodeUpc: upc,
      ...patch,
    }).catch((e) => console.warn("[MCMItemCard] correction teach failed:", e?.message || e));
  };
  const setName        = (v) => { setNameState(v);        update({ name: v }); };
  const setBrand       = (v) => { setBrandState(v);       update({ brand: v }); teachCorrection({ brand: v || null }); };
  const setCanonicalId = (v) => { setCanonicalIdState(v); update({ canonicalId: v }); teachCorrection({ canonicalId: v || null }); };
  const setTypeId      = (v) => { setTypeIdState(v);      update({ typeId: v }); teachCorrection({ typeId: v || null }); };
  const setTileId      = (v) => { setTileIdState(v);      update({ tileId: v }); teachCorrection({ tileId: v || null }); };
  const setLocation    = (v) => {
    // When location changes, the existing tileId is from the old
    // location's tile set (e.g. "dairy" is a Fridge tile, not a
    // Pantry tile) and would carry as a non-resolvable id into
    // the new location → kitchen view falls through to misc.
    // Clear tileId on the same patch so the new location starts
    // fresh and the user re-picks via "+ Pick a shelf".
    setLocationState(v);
    setTileIdState(null);
    update({ location: v, tileId: null });
    teachCorrection({ location: v || null });
  };
  const setStateAxis   = (v) => { setStateAxisState(v);   update({ state: v }); };
  const setCut         = (v) => { setCutState(v);         update({ cut: v }); };
  const setAmount      = (v) => {
    // Seal-break detection routes through the shared predicate so
    // gauge edits behave identically to consume / cook-mode decrements.
    // See `autoMigratePatchOnOpen` in helpers.js for the full rule.
    const migrate = autoMigratePatchOnOpen({
      prevAmount: amount,
      newAmount:  v,
      max,
      state:      stateAxis,
      location,
    });
    setAmountState(v);
    if (migrate) {
      // Snapshot pre-migration values so the toast's Undo button can
      // restore the user's original placement.
      const prevLocation = location;
      const prevTileId   = tileId;
      setLocationState(migrate.location);
      setTileIdState(migrate.tileId);
      update({ amount: v, location: migrate.location, tileId: migrate.tileId });
      toast.push(
        `Moved to fridge — opened ${openedNounForState(stateAxis)} keep longer cold.`,
        {
          kind: "info",
          emoji: "",
          undo: () => {
            setLocationState(prevLocation);
            setTileIdState(prevTileId);
            update({ location: prevLocation, tileId: prevTileId });
          },
        }
      );
    } else {
      update({ amount: v });
    }
  };
  const setUnit        = (v) => { setUnitState(v);        update({ unit: v }); };
  const setExpiresAt   = (v) => { setExpiresAtState(v);   update({ expiresAt: v }); };
  // Kid axis writes through a single onChange — the section may
  // batch (visible+kind on first toggle) or send single-field
  // patches (kidLabel debounce, kidKind chip pick).
  const setKidPatch    = (patch) => {
    if (patch.kidVisible !== undefined) setKidVisibleState(!!patch.kidVisible);
    if (patch.kidLabel   !== undefined) setKidLabelState(patch.kidLabel || null);
    if (patch.kidKind    !== undefined) setKidKindState(patch.kidKind || null);
    update(patch);
    // When flipping kid_visible ON, also pre-bake cook_instructions
    // via Claude so the parent can hit COOK from the Requests inbox
    // without waiting for AI to draft reheat steps. Skips if the
    // row already has instructions (manual or prior-generated).
    // Fire-and-forget; failure is silent — the EAT button in this
    // card still offers GENERATE WITH AI later as a fallback.
    if (patch.kidVisible === true && !liveCookInstructions && (canonicalId || name)) {
      (async () => {
        try {
          const result = await suggestCookInstructions({
            name, canonicalId, brand, state: stateAxis, cut, category: item.category,
          });
          const fresh = result?.cookInstructions;
          if (fresh && Array.isArray(fresh.steps) && fresh.steps.length > 0) {
            setLiveCookInstructions(fresh);
            update({ cookInstructions: fresh });
          }
        } catch (e) {
          console.warn("[kid-reheat] suggestCookInstructions threw:", e?.message || e);
        }
      })();
    }
  };

  // Esc closes (matches the legacy ItemCard).
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose && onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Body scroll lock — pins the body in place via position:fixed
  // so iOS can't scroll the page underneath while the sheet is
  // open. See lib/useBodyScrollLock for why overflow:hidden alone
  // doesn't work on iOS.
  useBodyScrollLock();

  // Identity helpers
  const ing      = canonicalId ? findIngredient(canonicalId) : null;
  const iconUrl  = canonicalImageUrlFor(canonicalId, null);
  const stateOpts = canonicalId ? (statesForIngredient(canonicalId) || []) : [];
  const cutOpts   = canonicalId ? (cutsForIngredient(canonicalId) || []) : [];

  // Derived display name — re-runs as the user mutates the
  // identity components below (brand chip / canonical pick /
  // state pick), so the headline reflects the current truth.
  // Falls through to free-text `name` only when no canonical is
  // bound (the genuine pre-canonical / scratch case).
  const derivedName = useMemo(
    () => buildDisplayName({
      name, brand, canonicalId, state: stateAxis, cut, max, unit,
    }),
    [name, brand, canonicalId, stateAxis, cut, max, unit]
  );

  // Claims (Organic / Grass-fed / Made in Italy / etc.) read
  // straight off the raw row — these aren't editable here in
  // v1. Empty array hides the strip entirely.
  const claims = useMemo(() => getItemClaims(item), [item]);
  // User's dietary preferences from profile — drives the dietary-
  // warning chip below the claims row. Walks item's full
  // compositional tree (canonicalId + ingredient_ids) and surfaces
  // any child whose existing diet flags / category violate the
  // user's preference.
  const { profile } = useProfile(userId);
  const dietWarnings = useMemo(
    () => dietaryWarningsForRow(item, profile, dbMap),
    [item, profile, dbMap],
  );

  // Days-until-expiry display (chip-style, color follows freshness)
  const daysToExpiry = (() => {
    if (!expiresAt) return null;
    const d = expiresAt instanceof Date ? expiresAt : new Date(expiresAt);
    if (Number.isNaN(d.getTime())) return null;
    return Math.ceil((d.getTime() - Date.now()) / 86400000);
  })();

  // Quantity-gauge math (mirrors KitchenCard's slider).
  const haveGauge = Number(max) > 0 && Number.isFinite(Number(amount));
  const pct = haveGauge ? Math.max(0, Math.min(100, (Number(amount) / Number(max)) * 100)) : 0;
  const sealed = haveGauge && Number(amount) >= Number(max) - 0.0001;
  const fillColor = sealed ? theme.color.teal : theme.color.burnt;
  const step = max <= 10 ? 0.1 : max <= 100 ? 1 : max / 100;
  const fmt = (n) => Number.isInteger(n) ? String(n) : Number(n).toFixed(1);

  return (
    <motion.div
      role="dialog"
      aria-modal="true"
      aria-label={`Edit ${item.name}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      // Backdrop fade matches the inner sheet's slide duration
      // (~360 ms) so the room dims out at the same rhythm the sheet
      // exits, instead of snapping dark before the slide finishes.
      exit={{ opacity: 0, transition: { duration: 0.36, ease: [0.32, 0.72, 0.4, 1] } }}
      transition={{ duration: 0.22, ease: [0.32, 0.72, 0.4, 1] }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 60,
        background: "rgba(20,12,4,0.55)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        // Block iOS from interpreting backdrop touches as
        // page-level pan/zoom. Combined with the body scroll lock
        // above, the page underneath has no path to receive any
        // gesture input while this surface is up.
        touchAction: "none",
        overscrollBehavior: "contain",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}
      onTouchMove={(e) => {
        // Belt-and-suspenders: if a touchmove originated on the
        // backdrop itself (not a sheet child), preventDefault to
        // make sure iOS doesn't try to fall through to the page.
        if (e.target === e.currentTarget) e.preventDefault();
      }}
    >
      <motion.div
        ref={sheetRef}
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        // Exit uses a longer ease-out tween instead of the entrance
        // spring — springs feel snappy on dismiss (the sheet flicks),
        // a 420 ms ease-out curve glides the sheet off-screen at a
        // pace that reads as "leaving" rather than "vanishing". The
        // curve is the iOS-standard outgoing-content ease.
        exit={{ y: "100%", transition: { duration: 0.42, ease: [0.32, 0.72, 0.4, 1] } }}
        transition={{ type: "spring", stiffness: 360, damping: 36 }}
        // Two ways to dismiss:
        //   * Tap the grabber pill at the top and pull — framer
        //     drag-follows the finger, releases past 120 px / 500
        //     v/s closes (visceral, deliberate gesture)
        //   * Scroll the content past the top — useSheetDismissAtTop
        //     watches for a 100 px downward pull at scrollTop=0
        //     (skipping interactive targets) and fires onClose
        //     directly, no drag handoff needed
        // dragListener={false} keeps framer from auto-capturing
        // every pointer event so the sheet body stays scrollable.
        drag="y"
        dragControls={dragControls}
        dragListener={false}
        dragConstraints={{ top: 0 }}
        dragElastic={{ top: 0, bottom: 0 }}
        dragMomentum={false}
        onDragEnd={(_event, info) => {
          const DRAG_DISMISS_PX  = 120;
          const DRAG_DISMISS_VEL = 500;
          if (info.offset.y > DRAG_DISMISS_PX || info.velocity.y > DRAG_DISMISS_VEL) {
            onClose && onClose();
          }
        }}
        {...dismissHandlers}
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: "env(safe-area-inset-bottom, 0px)",
          marginLeft: "auto",
          marginRight: "auto",
          width: "calc(100% - 24px)",
          maxWidth: 520,
          maxHeight: "90dvh",
          overflowY: "auto",
          // Prevent scroll-chaining: when the sheet's internal scroll
          // hits its top/bottom, iOS would otherwise hand the
          // remaining scroll to the page underneath. `contain` traps
          // it inside the sheet so the background never reacts.
          overscrollBehavior: "contain",
          scrollbarGutter: "stable",
          padding: space.gap,
          borderRadius: radius.panel,
          background: theme.color.glassFillHeavy,
          border: `1px solid ${theme.color.glassBorder}`,
          // Sheet blur dropped 24 → 12 — radius² compositing cost is
          // the dominant factor on iOS Safari, and the heavy
          // glassFill alpha already carries the frosted look.
          backdropFilter: "blur(12px) saturate(140%)",
          WebkitBackdropFilter: "blur(12px) saturate(140%)",
          boxShadow: "0 24px 60px rgba(20,12,4,0.40), 0 4px 16px rgba(20,12,4,0.20)",
          // Promote to its own compositor layer so the slide-in /
          // slide-out animation doesn't force iOS Safari to re-run
          // the backdrop-filter blur every frame against shifting
          // input pixels. The static blur is rasterized once on this
          // layer and the layer transforms via the GPU.
          willChange: "transform",
          ...THEME_TRANSITION,
        }}
      >
        {/* Top-right controls — staple ★ + close ✕. */}
        <div style={{
          position: "absolute",
          top: 10, right: 52,
          zIndex: 2,
        }}>
          <StapleStar canonicalId={canonicalId || item?.canonicalId || null} userId={userId} />
        </div>
        <button
          type="button"
          aria-label="Close"
          onClick={(e) => { e.stopPropagation(); onClose && onClose(); }}
          style={closeButtonStyle(theme)}
        >
          <span aria-hidden style={{ lineHeight: 1, fontSize: 18, marginTop: -1 }}>×</span>
        </button>
        {/* Drag handle — small grabber pill at the top of the sheet,
            iOS modal pattern. The wrapping div is a 24 px touch zone
            so the handle is forgiving to thumb misses. touch-action
            none prevents iOS from claiming the pointer for scroll
            before framer's drag controller starts. */}
        <div
          onPointerDown={(e) => dragControls.start(e)}
          aria-hidden
          style={{
            width: "100%",
            height: 22,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "grab",
            touchAction: "none",
            marginTop: -10,
            marginBottom: space.tight,
          }}
        >
          <span style={{
            width: 40,
            height: 4,
            borderRadius: 2,
            background: theme.color.inkFaint,
            opacity: 0.35,
          }} />
        </div>
        {/* Header — icon + name + right-rail axis pills. Editorial
            italic for the title so it reads as the same hand as the
            rest of the MCM surface. */}
        <div style={{ ...inline(space.flow), alignItems: "flex-start" }}>
          {iconUrl && (
            <div style={{ flexShrink: 0, paddingTop: 2 }}>
              <img
                src={iconUrl}
                alt=""
                style={{
                  width: 56, height: 56, objectFit: "contain",
                }}
              />
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <Kicker tone={theme.color.inkFaint}>Editing</Kicker>
            {/* Header name. When a canonical is bound, the title is
                DERIVED from identity components (brand + state +
                canonical + cut) per the CLAUDE.md hierarchy rule, so
                a typo'd raw.name doesn't fossilize as the visible
                title. Edits land via the identity rail at the bottom.
                When no canonical is bound (free-text / pre-canonical
                rows), the header stays a writable input — the
                user's typed text IS the row's identity until a
                canonical lands. */}
            {canonicalId ? (
              <div style={{
                fontFamily: font.itemTitle,
                fontStyle: "italic",
                fontWeight: 500,
                fontSize: 18,
                lineHeight: 1.15,
                letterSpacing: "0",
                color: theme.color.ink,
                marginTop: 2,
                whiteSpace: "normal",
                wordBreak: "break-word",
              }}>
                {derivedName}
              </div>
            ) : (
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Name this item"
                style={{
                  fontFamily: font.itemTitle,
                  fontStyle: "italic",
                  fontWeight: 500,
                  fontSize: 18,
                  lineHeight: 1.15,
                  letterSpacing: "0",
                  color: theme.color.ink,
                  width: "100%",
                  marginTop: 2,
                  background: "transparent",
                  border: "none",
                  outline: "none",
                  padding: 0,
                }}
              />
            )}
            {claims.length > 0 && (
              <div style={{
                marginTop: space.tight,
                display: "flex",
                flexWrap: "wrap",
                gap: 4,
              }}>
                {claims.map((claim) => (
                  <span
                    key={claim}
                    style={{
                      padding: "3px 8px",
                      fontFamily: font.detail,
                      fontStyle: "italic",
                      fontSize: 11,
                      lineHeight: 1.2,
                      color: theme.color.inkMuted,
                      background: theme.color.glassFillLight || "rgba(124,94,44,0.08)",
                      border: `1px solid ${theme.color.borderSubtle || "rgba(124,94,44,0.18)"}`,
                      borderRadius: 999,
                      letterSpacing: "0.01em",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {claim}
                  </span>
                ))}
              </div>
            )}
            {/* Dietary-warning chip strip — fires when any of the
                row's compositional ingredients (canonicalId +
                ingredient_ids) violates the user's profile.dietary
                preference. Inheritance via existing seed data: the
                cheese ingredient already has category="dairy", so a
                Cheese Frank row gets a "⚠ dairy · Cheese" chip for
                a dairy-free user without us relabeling cheese. */}
            {dietWarnings.length > 0 && (
              <div style={{
                marginTop: space.tight,
                display: "flex", flexWrap: "wrap", gap: 4,
              }}>
                {dietWarnings.map((w, i) => (
                  <span key={i} title={`${w.name} — ${w.reason}`} style={{
                    display: "inline-block",
                    padding: "2px 8px",
                    borderRadius: 999,
                    background: withAlpha(theme.color.burnt, 0.18),
                    color: theme.color.burnt,
                    border: `1px solid ${withAlpha(theme.color.burnt, 0.45)}`,
                    fontFamily: font.mono, fontSize: 10,
                    letterSpacing: "0.04em",
                    fontWeight: 600,
                  }}>
                    ⚠ {w.reason} · {w.name}
                  </span>
                ))}
              </div>
            )}
          </div>
          {/* EAT / EAT REHEAT — replaces the old category/tile pills
              row. Breadcrumb (rendered elsewhere on the card) already
              shows category + tile placement, so these were redundant.
              EAT is the primary action a user takes from this card
              once they've ID'd the item; surfacing it here keeps it
              one tap away. */}
          <button
            type="button"
            onClick={handleEatTap}
            className="mcm-focusable"
            style={{
              padding: "8px 14px",
              background: "transparent",
              border: `1px solid ${withAlpha("#D4A637", 0.55)}`,
              borderRadius: 999,
              color: "#D4A637",
              fontFamily: font.mono,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.12em",
              fontStyle: "italic",
              cursor: "pointer",
              whiteSpace: "nowrap",
              alignSelf: "center",
            }}
          >
            {hasCookableContext ? "EAT / REHEAT" : "EAT IT"}
          </button>
        </div>

        <hr style={{ ...divider, margin: `${space.flow}px 0` }} />

        {/* Quantity row — gauge + numeric readout. Slider sits
            inline (no toggle-to-reveal) since the editor is the
            place to fiddle; the grid card has the lighter
            tap-to-edit affordance. */}
        {haveGauge ? (
          <>
            <FieldKicker theme={theme}>
              Remaining {sealed ? "· sealed" : "· opened"}
            </FieldKicker>
            <div style={{ marginTop: space.tight }}>
              <div style={{
                height: 4,
                borderRadius: 2,
                background: withAlpha(theme.color.ink, 0.06),
                overflow: "hidden",
              }}>
                <div style={{
                  height: "100%", width: `${pct}%`,
                  background: fillColor,
                  boxShadow: `0 0 6px ${withAlpha(fillColor, 0.45)}`,
                  transition: "width 600ms cubic-bezier(0.22, 1, 0.36, 1), background 200ms ease",
                }} />
              </div>
              <div style={{ ...inline(space.tight), marginTop: space.tight }}>
                <input
                  type="range"
                  min="0"
                  max={Number(max)}
                  step={step}
                  value={Number(amount)}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  aria-label={`Estimate ${item.name} remaining`}
                  style={{ flex: 1, accentColor: fillColor }}
                />
                <span style={{
                  fontFamily: font.mono, fontSize: 11,
                  color: theme.color.inkMuted,
                  minWidth: 80, textAlign: "right", whiteSpace: "nowrap",
                }}>
                  {fmt(Number(amount))} / {fmt(Number(max))} {unit || ""}
                </span>
              </div>
            </div>
          </>
        ) : (
          <>
            <FieldKicker theme={theme}>Package size</FieldKicker>
            <div style={{ ...inline(space.tight), marginTop: space.tight }}>
              <input
                type="number"
                inputMode="decimal"
                value={amount ?? ""}
                onChange={(e) => {
                  const n = e.target.value === "" ? null : Number(e.target.value);
                  setAmount(n);
                  // No max set yet — set max = amount on first edit so
                  // the row promotes from quantityless to gauge-able.
                  if (n != null && (max == null || max <= 0)) {
                    setMaxState(n);
                    update({ max: n });
                  }
                }}
                placeholder="0"
                style={{ ...field, flex: 1 }}
              />
              <button
                type="button"
                onClick={() => setPickerOpen("unit")}
                style={chipBtn(theme, axis.canonical, false)}
              >
                {unit || "+ Pick unit"}
              </button>
            </div>
          </>
        )}

        <hr style={{ ...divider, margin: `${space.gap}px 0 ${space.flow}px` }} />

        {/* Location segment — fridge / pantry / freezer with the
            tile picker chip stacked underneath. */}
        <FieldKicker theme={theme}>Where it lives</FieldKicker>
        <div style={{ ...stack(space.tight), marginTop: space.tight }}>
          <div style={{ ...inline(space.tight) }}>
            {LOCATIONS.map((loc) => {
              const active = location === loc.id;
              return (
                <button
                  key={loc.id}
                  type="button"
                  onClick={() => setLocation(loc.id)}
                  style={{
                    flex: 1,
                    fontFamily: font.serif,
                    fontStyle: "italic",
                    fontSize: 14,
                    color: active ? theme.color.ink : theme.color.inkMuted,
                    background: active
                      ? withAlpha(axis.storedIn, 0.18)
                      : "transparent",
                    border: `1px solid ${active ? withAlpha(axis.storedIn, 0.55) : theme.color.hairline}`,
                    borderRadius: radius.field,
                    padding: `${space.tight}px ${space.inline}px`,
                    cursor: "pointer",
                    transition: "background 200ms ease, border-color 200ms ease",
                  }}
                >
                  <span style={{ ...inline(space.tight), justifyContent: "center" }}>
                    <span style={{
                      width: 8, height: 8, borderRadius: 999,
                      background: LOCATION_DOT[loc.id] || theme.color.inkFaint,
                      opacity: active ? 1 : 0.5,
                      flexShrink: 0,
                    }} />
                    {loc.label}
                  </span>
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => setPickerOpen("tile")}
            style={chipBtn(theme, axis.storedIn, !tileId)}
          >
            {tileId
              ? `Stored in · ${
                  LOCATIONS.find(l => l.id === location)?.tiles.find(t => t.id === tileId)?.label
                  || tileId
                }`
              : "+ Pick a shelf"}
          </button>
        </div>

        <hr style={{ ...divider, margin: `${space.gap}px 0 ${space.flow}px` }} />

        {/* Expires — single chip. Tapping opens the preset picker
            (auto / none / today / Nd / Nw / Nm / 1y) reused from
            AddDraftPickers. */}
        <FieldKicker theme={theme}>Expires</FieldKicker>
        <div style={{ ...inline(space.tight), marginTop: space.tight }}>
          <button
            type="button"
            onClick={() => setPickerOpen("expires")}
            style={chipBtn(theme, axis.state, !expiresAt)}
          >
            {(() => {
              if (expiresAt instanceof Date) {
                const formatted = expiresAt.toLocaleDateString(undefined, {
                  month: "short", day: "numeric", year: "numeric",
                });
                return `Expires · ${formatted}`;
              }
              if (expiresAt === null) return "Doesn't expire";
              if (expiresAt === "auto") return "Auto · from canonical";
              return "+ Set expiry";
            })()}
          </button>
          {daysToExpiry != null && (
            <span style={{
              fontFamily: font.mono, fontSize: 11,
              color: daysChipColor(daysToExpiry, theme),
              whiteSpace: "nowrap",
            }}>
              {formatDaysChip(daysToExpiry)}
            </span>
          )}
        </div>

        {/* === Product information ====================================
            Read-only zone. Sits below the editable fields because the
            user works on their stuff first (rename, quantity, expiry)
            and reads the canonical's stuff second. Sections gated on
            data presence so empty enrichment doesn't clutter; on a
            free-text row with no canonical, this whole block stays
            silent. */}
        {hasInfo ? (
          <>
            <hr style={{ ...divider, margin: `${space.gap}px 0 ${space.flow}px` }} />
            {/* Nutrition scan CTA — sits directly above the
                NutritionRow so the affordance is contextual to the
                section it updates. Tap to OCR a Nutrition Facts
                panel, review/edit in NutritionOverrideSheet, save
                writes the row's override + teaches brand_nutrition. */}
            <button
              type="button"
              onClick={() => setNutritionPhase("scanner")}
              className="mcm-focusable"
              style={{
                ...ghostButton,
                width: "100%",
                justifyContent: "center",
                marginBottom: space.flow,
                color: theme.color.ink,
                border: `1px dashed ${withAlpha(theme.color.ink, 0.25)}`,
                background: "transparent",
                fontFamily: font.mono,
                fontSize: 11,
                letterSpacing: "0.14em",
                fontWeight: 600,
              }}
            >
              📸  SCAN NUTRITION LABEL
            </button>
            {enrichment.nutrition && (
              <NutritionRow nutrition={enrichment.nutrition} theme={theme} />
            )}
            {enrichment.description && (
              <AboutSection
                description={enrichment.description}
                flavorProfile={enrichment.flavorProfile}
                theme={theme}
              />
            )}
            {enrichment.prepTips && (
              <TipSection tip={enrichment.prepTips} theme={theme} />
            )}
            {Array.isArray(enrichment.pairs) && enrichment.pairs.length > 0 && (
              <PairsSection pairs={enrichment.pairs} theme={theme} />
            )}
            {/* History / cultural sections — practical info first, fun
                reading at the bottom. Origin is the biographic chip,
                Story is the lyrical block. */}
            {enrichment.origin && (
              <OriginSection origin={enrichment.origin} theme={theme} />
            )}
            {enrichment.culturalNotes && (
              <StorySection note={enrichment.culturalNotes} theme={theme} />
            )}
          </>
        ) : canonicalId ? (
          <>
            <hr style={{ ...divider, margin: `${space.gap}px 0 ${space.flow}px` }} />
            <div style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              padding: "10px 0",
            }}>
              <div style={{
                fontFamily: "'DM Mono', ui-monospace, monospace",
                fontSize: 10,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: theme.color.inkMuted,
              }}>
                No info yet
              </div>
              <div style={{
                fontFamily: "'forma-djr-deck', system-ui, sans-serif",
                fontSize: 13,
                lineHeight: 1.4,
                color: theme.color.inkMuted,
              }}>
                Description, prep tips, nutrition, and pairings haven't been
                generated for this canonical. Tap to draft them now —
                lands in the admin queue for review.
              </div>
              <div>
                <EnrichmentButton
                  canonicalId={canonicalId}
                  pantryItemId={item?.id || null}
                  compact
                />
              </div>
            </div>
          </>
        ) : null}

        <hr style={{ ...divider, margin: `${space.gap}px 0 ${space.flow}px` }} />

        {/* Identity rail — brand + canonical chips. Sits at the
            bottom of the editable zone so the user works through
            the row's mutable state first (rename, quantity, where
            it lives, expiry) and only revisits identity when they
            need to (which is rarely, after the canonical lands).
            Chips here rather than full inputs because identity is
            nominal: the user isn't typing, they're picking from
            the registry. */}
        <FieldKicker theme={theme}>Identity</FieldKicker>
        <div style={{ ...inline(space.tight), flexWrap: "wrap", marginTop: space.tight }}>
          <AxisChip
            theme={theme}
            tone={theme.color.inkMuted}
            label={brand ? `Brand · ${brand}` : "+ Add brand"}
            dashed={!brand}
            onClick={() => setBrandEditing(true)}
          />
          <AxisChip
            theme={theme}
            tone={axis.canonical}
            label={ing ? `Canonical · ${ing.name}` : "+ Pick canonical"}
            dashed={!ing}
            onClick={() => setPickerOpen("canonical")}
          />
          {cutOpts.length > 0 && (
            <AxisChip
              theme={theme}
              tone={axis.cut}
              label={cut
                ? `Cut · ${cutLabel(cut) || cut}`
                : "+ Pick cut"}
              dashed={!cut}
              onClick={() => setPickerOpen("cut")}
            />
          )}
          {stateOpts.length > 0 && (
            <AxisChip
              theme={theme}
              tone={axis.state}
              label={stateAxis
                ? `State · ${STATE_LABELS[stateAxis] || stateAxis}`
                : "+ Set state"}
              dashed={!stateAxis}
              onClick={() => setPickerOpen("state")}
            />
          )}
        </div>

        {/* Kid-requestable axis — flag this specific pantry row as
            something the kids can request from their Foods tab, with
            an optional nickname + Meal/Snack/Treat tag. Mirrors the
            controls in KidPantryManager but inline so it isn't
            buried under Settings → Kids Mode. */}
        <KidRequestableSection
          theme={theme}
          displayName={buildDisplayName({ ...item, name, brand, canonicalId }) || name}
          canonicalId={canonicalId}
          kidVisible={kidVisible}
          kidLabel={kidLabel}
          kidKind={kidKind}
          onChange={setKidPatch}
        />

        {/* Identity breadcrumb — full stack at the BOTTOM of the
            card so the user can verify what landed on the row WITHOUT
            it competing for attention with the title / pills at the
            top. Order matches CLAUDE.md identity hierarchy from
            coarsest (where it lives) to finest (size):
              Stored in → Category → Hub chain → Canonical
                → Brand → Flavor variant → Package size
            Each slot is a small chip with axis-tinted color; missing
            slots hide so the strip stays compact. Hub chain walks
            parentHub recursively so a canonical under a nested hub
            (e.g. shiitake → mushroom_hub → vegetable_hub) shows all
            tiers. Synthetic canonicals read info.hub for their hub. */}
        <div style={{ marginTop: space.gap }}>
          <IdentityBreadcrumb
            theme={theme}
            tileId={tileId}
            location={location}
            category={item.category}
            canonicalId={canonicalId}
            canonicalName={canonicalId ? (findIngredient(canonicalId)?.name || canonicalId) : null}
            brand={brand}
            variant={item.attributes?.variant || null}
            claims={item.attributes?.claims || null}
            packageMax={max}
            packageUnit={unit}
            dbMap={dbMap}
          />
        </div>

        <hr style={{ ...divider, margin: `${space.block}px 0 ${space.flow}px` }} />

        {/* Footer — destructive on the left, EAT mid, primary on
            the right. Delete arms on first tap (border + label flip
            burnt) and commits on second; this protects against stray
            finger mash on a card the user only meant to view. */}
        <div style={{ ...inline(space.flow), justifyContent: "space-between" }}>
          <button
            type="button"
            onClick={() => {
              if (!deleteArmed) { setDeleteArmed(true); return; }
              onDelete && onDelete();
            }}
            style={{
              ...ghostButton,
              fontStyle: "italic",
              color: deleteArmed ? "#FFF8EE" : theme.color.burnt,
              background: deleteArmed
                ? "linear-gradient(180deg, #C85A1F 0%, #A34711 100%)"
                : "transparent",
              border: deleteArmed
                ? "1px solid rgba(255,255,255,0.35)"
                : `1px solid ${withAlpha(theme.color.burnt, 0.45)}`,
            }}
          >
            {deleteArmed ? "Tap again to delete" : "Delete"}
          </button>
          <button
            type="button"
            onClick={() => onClose && onClose()}
            style={ctaButton}
          >
            Done
          </button>
        </div>
      </motion.div>

      {/* Eat flow — three sequential surfaces. EatIntentSheet only
          mounts for cookable rows (leftovers); raw ingredients skip
          straight to IAteThisSheet. ReheatMode is the middle stop
          when the user opts in to reheat first. */}
      {eatPhase === "intent" && (
        <EatIntentSheet
          item={{ emoji: item.emoji, name: item.name || item.canonicalName }}
          hasReheat={!!liveCookInstructions}
          summary={liveCookInstructions ? formatReheatSummary(liveCookInstructions) : null}
          onReheat={() => setEatPhase("reheat")}
          onGenerate={handleGenerateReheat}
          onJustLog={() => setEatPhase("log")}
          onClose={() => setEatPhase(null)}
        />
      )}
      {eatPhase === "reheat" && reheatRecipe && (
        <ReheatMode
          recipe={reheatRecipe}
          emoji={item.emoji}
          onFinish={() => setEatPhase("log")}
          onExit={() => setEatPhase(null)}
        />
      )}
      {eatPhase === "log" && (
        <IAteThisSheet
          pantryRow={item}
          userId={userId}
          onClose={() => setEatPhase(null)}
          onDone={() => {
            setEatPhase(null);
            onClose && onClose();
          }}
        />
      )}

      {/* Nutrition label scan flow. Scanner → Override review → SAVE. */}
      {nutritionPhase === "scanner" && (
        <NutritionLabelScanner
          item={item}
          onClose={() => setNutritionPhase(null)}
          onComplete={handleNutritionScanComplete}
          onManualFallback={handleNutritionScanManualFallback}
        />
      )}
      {nutritionPhase === "override" && (
        <NutritionOverrideSheet
          item={item}
          onClose={() => {
            setNutritionPhase(null);
            setNutritionScanPayload(null);
          }}
          onSave={handleNutritionSave}
          initialValues={nutritionScanPayload?.nutritionBlock || null}
          initialPer={nutritionScanPayload?.nutritionBlock?.per || null}
          provenance={nutritionScanPayload ? "label_scan" : null}
          packageInfo={nutritionScanPayload?.packageInfo || null}
          photoPreviewUrl={nutritionScanPayload?.photoPreviewUrl || null}
          scanId={nutritionScanPayload?.scanId || null}
        />
      )}

      {/* Pickers — reused from AddDraftSheet so the visual language
          matches across add and edit. The override-setter args are
          stubbed (we don't need cascade locking when editing an
          existing row). */}
      <AddDraftPickers
        pickerOpen={pickerOpen}
        setPickerOpen={setPickerOpen}
        theme={theme}
        typeId={typeId}
        setTypeId={setTypeId}
        setTypeOverridden={setOverridden}
        canonicalId={canonicalId}
        unit={unit}
        setUnit={setUnit}
        state={stateAxis}
        setState={setStateAxis}
        setStateOverridden={setOverridden}
        cut={cut}
        setCut={setCut}
        expiresAt={expiresAt}
        setExpiresAt={setExpiresAt}
        autoDays={autoDays}
        location={location}
        tileId={tileId}
        setTileId={setTileId}
        setTileOverridden={setOverridden}
        setCanonicalId={setCanonicalId}
        setCanonicalOverridden={setOverridden}
        allCanonicals={allCanonicals}
      />

      {brandEditing && (
        <BrandPickerSheet
          canonicalId={canonicalId || null}
          suggestions={[]}
          onPick={(b) => { setBrandEditing(false); setBrand(b); }}
          onClose={() => setBrandEditing(false)}
        />
      )}
    </motion.div>
  );
}

// Small subhelpers — kept inline so this file is self-contained
// and doesn't reach into AddDraftSheet's internal primitives.

function FieldKicker({ theme, children }) {
  return (
    <div style={{
      fontFamily: font.mono,
      fontSize: 10,
      fontWeight: 500,
      letterSpacing: "0.18em",
      textTransform: "uppercase",
      color: theme.color.inkFaint,
    }}>
      {children}
    </div>
  );
}

function AxisChip({ theme, tone, label, dashed, onClick }) {
  return (
    <button type="button" onClick={onClick} style={chipBtn(theme, tone, dashed)}>
      {label}
    </button>
  );
}

// Nutrition row — single editorial line of peer-sized data, NOT
// the SaaS "hero metric + supporting stats" template. The kcal sits
// inline with the macros separated by middle dots; micros (fiber,
// sodium) get a quieter second line. Per-anchor (100g / count /
// serving) goes in the kicker so the numbers below carry weight
// alone.
function NutritionRow({ nutrition, theme }) {
  const per = nutrition.per || "100g";
  const perLabel = per === "count"
    ? "per item"
    : per === "serving"
      ? "per serving"
      : `per ${per}`;
  const macros = [];
  if (nutrition.protein_g != null) macros.push(`${formatGram(nutrition.protein_g)} protein`);
  if (nutrition.carb_g    != null) macros.push(`${formatGram(nutrition.carb_g)} carbs`);
  if (nutrition.fat_g     != null) macros.push(`${formatGram(nutrition.fat_g)} fat`);
  const micros = [];
  if (nutrition.fiber_g  != null) micros.push(`${formatGram(nutrition.fiber_g)} fiber`);
  if (nutrition.sodium_mg != null) micros.push(`${nutrition.sodium_mg}mg sodium`);
  const kcalCell = nutrition.kcal != null ? `${Math.round(nutrition.kcal)} kcal` : null;
  const primaryParts = [kcalCell, ...macros].filter(Boolean);
  if (primaryParts.length === 0 && micros.length === 0) return null;
  return (
    <section style={{ marginTop: space.flow }}>
      <FieldKicker theme={theme}>Nutrition · {perLabel}</FieldKicker>
      <div style={{
        ...stack(space.tight),
        marginTop: space.tight,
        fontFamily: font.sans,
        fontWeight: 500,
        fontSize: 15,
        color: theme.color.ink,
        letterSpacing: "0.005em",
      }}>
        {primaryParts.length > 0 && (
          <div>{primaryParts.join(" · ")}</div>
        )}
        {micros.length > 0 && (
          <div style={{ color: theme.color.inkMuted, fontSize: 14 }}>
            {micros.join(" · ")}
          </div>
        )}
      </div>
    </section>
  );
}

// About — description in editorial italic, then flavorProfile as a
// quieter second voice when available. Both capped at prose.measure
// (62ch) so a wide sheet doesn't stretch the line past the comfort
// reading band.
function AboutSection({ description, flavorProfile, theme }) {
  return (
    <section style={{ marginTop: space.gap }}>
      <FieldKicker theme={theme}>About</FieldKicker>
      <p style={{
        marginTop: space.tight,
        marginBottom: 0,
        fontFamily: font.sans,
        fontWeight: 500,
        fontSize: 15,
        lineHeight: 1.55,
        color: theme.color.ink,
        maxWidth: prose.measure,
      }}>
        {description}
      </p>
      {flavorProfile && (
        <p style={{
          marginTop: space.tight,
          marginBottom: 0,
          fontFamily: font.sans,
          fontWeight: 400,
          fontSize: 13.5,
          lineHeight: 1.5,
          color: theme.color.inkMuted,
          maxWidth: prose.measure,
        }}>
          {flavorProfile}
        </p>
      )}
    </section>
  );
}

// Tip — Fraunces italic, slightly smaller than About so it reads
// as supporting copy rather than competing for the same beat.
function TipSection({ tip, theme }) {
  return (
    <section style={{ marginTop: space.gap }}>
      <FieldKicker theme={theme}>Tip</FieldKicker>
      <p style={{
        marginTop: space.tight,
        marginBottom: 0,
        fontFamily: font.sans,
        fontWeight: 500,
        fontSize: 14.5,
        lineHeight: 1.55,
        color: theme.color.ink,
        maxWidth: prose.measure,
      }}>
        {tip}
      </p>
    </section>
  );
}

// Pairs — comma-separated list of canonical names, capped at 8 to
// keep the line single-row on most viewports. Resolves ids to
// display names so the row reads as English, not slugs.
function PairsSection({ pairs, theme }) {
  const names = pairs
    .slice(0, 8)
    .map(id => findIngredient(id)?.name || prettifySlug(id))
    .filter(Boolean);
  if (names.length === 0) return null;
  return (
    <section style={{ marginTop: space.gap }}>
      <FieldKicker theme={theme}>Pairs with</FieldKicker>
      <p style={{
        marginTop: space.tight,
        marginBottom: 0,
        fontFamily: font.sans,
        fontWeight: 400,
        fontSize: 14,
        color: theme.color.inkMuted,
        maxWidth: prose.measure,
      }}>
        {names.join(", ")}
      </p>
    </section>
  );
}

function OriginSection({ origin, theme }) {
  return (
    <section style={{ marginTop: space.gap }}>
      <FieldKicker theme={theme}>Origin</FieldKicker>
      <p style={{
        marginTop: space.tight,
        marginBottom: 0,
        fontFamily: font.sans,
        fontWeight: 400,
        fontSize: 13.5,
        lineHeight: 1.55,
        color: theme.color.inkMuted,
        maxWidth: prose.measure,
      }}>
        {origin}
      </p>
    </section>
  );
}

function StorySection({ note, theme }) {
  return (
    <section style={{ marginTop: space.gap }}>
      <FieldKicker theme={theme}>The Story</FieldKicker>
      <p style={{
        marginTop: space.tight,
        marginBottom: 0,
        fontFamily: font.sans,
        fontWeight: 500,
        fontSize: 14.5,
        lineHeight: 1.6,
        color: theme.color.ink,
        maxWidth: prose.measure,
      }}>
        {note}
      </p>
    </section>
  );
}

function formatGram(n) {
  if (n == null) return "0g";
  const num = Number(n);
  if (Number.isInteger(num)) return `${num}g`;
  return `${num.toFixed(1)}g`;
}

function prettifySlug(id) {
  return String(id || "").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

// Walk the hub chain for a canonical, returning slots from outermost
// (top-level hub) to innermost (the canonical's direct hub). Empty
// for canonicals with no hub binding. Synthetic canonicals carry their
// hub on info.hub (a string slug) — when that slug matches a registered
// HUBS entry we walk its parentHub chain too; otherwise we surface the
// raw string as a single-tier hub.
function buildHubChain(canonicalId, dbInfo) {
  if (!canonicalId) return [];
  const ing = findIngredient(canonicalId);
  // Bundled-canonical path — `hubForIngredient` returns the HUB
  // OBJECT (not just an id), so extract `.id` for the chain walk.
  // Earlier this code treated the return value as an id directly,
  // which made `findHub(object)` fail downstream → fell to the
  // synthetic "ai" branch with `prettifySlug(object)` rendering
  // as "[object Object]" in the breadcrumb.
  let startHubId = null;
  if (ing?.parentId) {
    startHubId = ing.parentId;
  }
  // Synthetic-canonical path — info.hub came from the propose flow's
  // parentHub field. Same chain walk if it resolves to a real hub.
  // Defensive: only accept string ids, not nested objects.
  if (!startHubId && dbInfo?.hub && typeof dbInfo.hub === "string") {
    startHubId = dbInfo.hub;
  }
  if (!startHubId) return [];

  // Walk parentHub up to 5 tiers as a safety cap. Most chains are 1-2
  // tiers in practice (cheese; mushroom_hub → vegetable_hub).
  const chain = [];
  let current = startHubId;
  for (let i = 0; i < 5 && current; i += 1) {
    const hub = findHub(current);
    if (hub) {
      chain.unshift({ id: hub.id, label: hub.name || prettifySlug(hub.id), source: "bundled" });
      current = hub.parentHub || null;
    } else {
      // Synthetic hub that isn't in the registered HUBS list — surface
      // it raw and stop walking (no parentHub we can resolve).
      chain.unshift({ id: current, label: prettifySlug(current), source: "ai" });
      current = null;
    }
  }
  return chain;
}

function IdentityBreadcrumb({
  theme, tileId, location, category,
  canonicalId, canonicalName,
  brand, variant, claims, packageMax, packageUnit, dbMap,
}) {
  const dbInfo = canonicalId ? (dbMap?.[canonicalId] || null) : null;
  const hubChain = buildHubChain(canonicalId, dbInfo);
  const sizeLabel = (packageMax != null && packageUnit) ? `${packageMax} ${packageUnit}` : null;

  // FLAVOR slot disabled. The yellow "claims" chip rendered here
  // ("Classic Norwegian", "Traditional", etc.) was repeatedly
  // requested gone — it surfaced demoted-from-name tokens that
  // weren't useful for shopping or recipe-matching, and the yellow
  // axis.ingredients tone was unreadable on cream backgrounds.
  // Variant + claims are still STORED on the row; just not
  // surfaced visually.
  const flavorLabel = null;

  // Collect every slot that has a value. Keep the order the user
  // specified: stored in → category → hub chain → canonical → brand
  // → flavor → size. Empty slots are skipped.
  const slots = [];
  if (tileId) {
    slots.push({ kicker: "STORED IN", value: prettifySlug(tileId), tone: axis.storedIn });
  }
  // CATEGORY chip dropped — was always redundant with the TILE chip
  // (DAIRY/DAIRY pair) and confused the user about why the top
  // "Cheese" typeId pill didn't sync. The HUB chip below covers
  // the within-tile family (Cheese/Milk/Butter), and TILE covers
  // the shelf bucket (Dairy & Eggs). Two coordinate axes is enough.
  for (const hub of hubChain) {
    slots.push({
      kicker: hub.source === "ai" ? "HUB · AI" : "HUB",
      value: hub.label,
      tone: axis.canonical,
    });
  }
  if (canonicalName) {
    slots.push({ kicker: "CANONICAL", value: canonicalName, tone: axis.canonical });
  }
  if (brand) {
    slots.push({ kicker: "BRAND", value: brand, tone: axis.canonical });
  }
  if (flavorLabel) {
    slots.push({ kicker: "FLAVOR", value: flavorLabel, tone: axis.ingredients });
  }
  if (sizeLabel) {
    slots.push({ kicker: "SIZE", value: sizeLabel, tone: axis.canonical });
  }

  if (slots.length === 0) return null;

  return (
    <div style={{
      marginTop: space.tight,
      display: "flex", flexWrap: "wrap", alignItems: "center", gap: 4,
    }}>
      {slots.map((slot, i) => (
        <BreadcrumbChip
          key={`${slot.kicker}-${i}`}
          kicker={slot.kicker}
          value={slot.value}
          tone={slot.tone}
          showArrow={i < slots.length - 1}
        />
      ))}
    </div>
  );
}

function BreadcrumbChip({ kicker, value, tone, showArrow }) {
  return (
    <>
      <span style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        padding: "2px 7px",
        background: withAlpha(tone, 0.10),
        border: `1px solid ${withAlpha(tone, 0.32)}`,
        borderRadius: 5,
        fontFamily: font.sans, fontSize: 9, fontWeight: 600,
        color: tone,
        letterSpacing: "0.10em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}>
        <span style={{ opacity: 0.55 }}>{kicker}</span>
        <span>{value}</span>
      </span>
      {showArrow && (
        <span style={{
          fontFamily: font.sans, fontSize: 10,
          color: withAlpha(tone, 0.45),
        }}>›</span>
      )}
    </>
  );
}

// Small ✕ in the top-right corner. Subtle by default — theme.inkFaint
// on a translucent ink-tinted ground — so it doesn't compete with the
// editable content; tap target is 32px even though the visual chip is
// 26px so it stays forgiving on thumb misses.
function closeButtonStyle(theme) {
  return {
    position: "absolute",
    top: 10,
    right: 10,
    width: 32,
    height: 32,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 999,
    border: `1px solid ${withAlpha(theme.color.ink, 0.10)}`,
    background: withAlpha(theme.color.ink, 0.06),
    color: theme.color.inkMuted,
    fontFamily: "system-ui, -apple-system, sans-serif",
    fontWeight: 500,
    cursor: "pointer",
    padding: 0,
    zIndex: 2,
  };
}

function chipBtn(theme, tone, dashed) {
  return {
    fontFamily: font.serif,
    fontStyle: "italic",
    fontWeight: 500,
    fontSize: 14,
    color: theme.color.ink,
    background: dashed ? "transparent" : withAlpha(tone, 0.18),
    border: dashed
      ? `1px dashed ${withAlpha(tone, 0.55)}`
      : `1px solid ${withAlpha(tone, 0.55)}`,
    borderRadius: 999,
    padding: `${space.tight}px ${space.flow}px`,
    cursor: "pointer",
    whiteSpace: "nowrap",
    transition: "background 200ms ease, border-color 200ms ease",
  };
}

export default MCMItemCard;
